/* =====================================================================
 * scroll-reveal.js v2（2026-05-27）
 *
 * 取代 v1 hard-code REVEAL_SELECTOR / HEADER_SELECTOR 模式，改走 data-reveal API：
 *   - 任何元素掛 data-reveal="<type>" 即進場觀察
 *   - data-reveal-stagger="N" 指定 stagger 序號（從 0 開始）
 *   - data-reveal-stagger-gap="tight|loose" 切 stagger 間距（預設 base）
 *
 * 觸發機制：
 *   - IntersectionObserver threshold 0.15 + rootMargin '0px 0px -10% 0px'
 *   - 一次性 reveal（unobserve after first intersect）
 *   - 頁底保險網：捲到接近文件底時強制 reveal 殘留元素（解 rootMargin -10%
 *     底邊死區害死「貼齊頁底的短元素」如 footer © 版權列，數學上過不了
 *     threshold 的結構性陷阱）
 *   - prefers-reduced-motion 直接顯示，不觀察
 *
 * 動態內容支援：
 *   - export window.__reobserveReveal() — render-cards.js 等動態 render 後呼叫
 *
 * 委派進場（data-reveal-defer）：
 *   - 掛 data-reveal-defer 的元素仍用 [data-reveal] 的 CSS 進場效果與 PRM fallback，
 *     但「何時 reveal」交給其他模組自管（如 carousel.js 依水平捲動逐張 reveal clinic 卡）。
 *   - 本檔的 page-scroll observer / reobserve / 頁底保險網一律跳過 [data-reveal-defer]，
 *     避免兩個 reveal 來源搶寫同一元素。PRM 直通分支不受影響（仍直接顯示，見下）。
 *
 * 保留（不動）：
 *   - hero page-load stagger（.hero__content.is-hero-revealed）
 *   - lazy image fade-in（img[loading="lazy"].is-loaded）
 * ===================================================================== */

// P-H-3：改 import 共用 prefersReducedMotion()（utils/dom.js），去重原 inline matchMedia 判斷。
import { prefersReducedMotion } from './utils/dom.js';

const PRM = prefersReducedMotion();

// page-scroll observer 負責的元素：排除委派給其他模組自管的 [data-reveal-defer]
const PAGE_REVEAL_SELECTOR = '[data-reveal]:not([data-reveal-defer])';

/* ---------- helpers ---------- */

/**
 * 套用 data-reveal-stagger attribute 到 CSS variable（reveal.css 用 calc 計算 delay）
 * CSS attr() 對非字串值不可靠，所以走 JS inline set。
 */
const applyStaggerVar = (el) => {
  if (el.hasAttribute('data-reveal-stagger')) {
    const idx = parseInt(el.dataset.revealStagger, 10);
    if (!Number.isNaN(idx)) {
      el.style.setProperty('--data-stagger', idx);
    }
  }
};

/* ---------- observer ---------- */

let observer = null;

/**
 * 標記元素進場 + 停止觀察（observer 與頁底保險網共用，避免重複邏輯）。
 */
const reveal = (el) => {
  el.classList.add('is-revealed');
  if (observer) {
    observer.unobserve(el);
  }
};

if (!PRM && 'IntersectionObserver' in window) {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        reveal(entry.target);
      }
    });
  }, {
    threshold: 0.15,
    rootMargin: '0px 0px -10% 0px',
  });
}

/* ---------- 頁底保險網 ----------
 * rootMargin 底邊 -10% 死區 + 一次性 unobserve，對「文件最後一個短元素」
 * （footer © 版權列）是結構性陷阱：它貼齊頁底、又矮，捲到底物理上仍進不了
 * 縮小後的有效 root，intersection ratio 永遠 0 → 永久卡 opacity:0。
 *
 * 解法：監聽 scroll，當捲到接近文件底（剩餘 < BOTTOM_THRESHOLD_PX）時，
 * 強制 reveal 所有殘留 [data-reveal]:not(.is-revealed)。一般元素早在觸底前
 * 就被 observer 接管，保險網不會碰到它們 → 不影響已正常的 section。
 * 全部 reveal 後即移除 listener（cleanup），無殘留監聽。
 */
const BOTTOM_THRESHOLD_PX = 120;

if (observer) {
  const flushBottom = () => {
    const remaining = document.querySelectorAll(`${PAGE_REVEAL_SELECTOR}:not(.is-revealed)`);
    if (!remaining.length) {
      window.removeEventListener('scroll', onScroll);
      return;
    }
    const scrollBottom = window.scrollY + window.innerHeight;
    const docHeight = document.documentElement.scrollHeight;
    if (docHeight - scrollBottom <= BOTTOM_THRESHOLD_PX) {
      remaining.forEach((el) => {
        applyStaggerVar(el);
        reveal(el);
      });
      window.removeEventListener('scroll', onScroll);
    }
  };

  // rAF 節流：scroll 高頻，避免每幀重複查 DOM
  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      flushBottom();
      ticking = false;
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
}

/* ---------- initial observe ---------- */

// PRM / 老瀏覽器直通分支用全集 [data-reveal]（含 defer），確保 deferred 元素在
// 無動畫情境下也直接顯示，不依賴自管模組（carousel.js 的 PRM 分支同樣會直通，重複
// 設 is-revealed 無害且冪等）。observer 分支只接非 defer 元素。
if (PRM || !('IntersectionObserver' in window)) {
  document.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
} else {
  const initialEls = document.querySelectorAll(PAGE_REVEAL_SELECTOR);
  initialEls.forEach((el) => {
    applyStaggerVar(el);
    observer.observe(el);
  });

  /* ---------- 純加法保險網 a：on-load 立即 reveal 初始視口內元素 ----------
   * IO 進視口 callback 是非同步排程；某些情境（fullPage 截圖不捲動、首屏渲染抓拍、
   * 部分瀏覽器 IO 排程延後）first-paint 時 above-the-fold 的 [data-reveal] 可能短暫
   * 卡在 opacity:0。這裡在 init 後立刻把「幾何上已落在視口內」的元素直接 reveal，
   * 不等 IO 回呼。reveal() 冪等（add class + unobserve），與隨後可能到的 IO 回呼不衝突；
   * below-fold 元素不符視口判斷、原樣交還 IO 捲動進場，既有行為不變。 */
  const revealInViewportNow = () => {
    const vh = window.innerHeight || document.documentElement.clientHeight;
    initialEls.forEach((el) => {
      if (el.classList.contains('is-revealed')) return;
      const rect = el.getBoundingClientRect();
      // 元素任一部分落在視口垂直範圍內即視為初始可見
      if (rect.top < vh && rect.bottom > 0) {
        reveal(el);
      }
    });
  };
  // 等版面穩定（字體/圖片 reflow）後量一次，避免 rect 量在 layout 前
  requestAnimationFrame(revealInViewportNow);

  /* ---------- 純加法保險網 b：全域 safety timeout ----------
   * 任何原因（IO 回呼始終不觸發、JS 中途錯誤打斷捲動監聽、自動化抓拍環境）導致殘留
   * [data-reveal] 永停 opacity:0 時，作為最後保險，逾時把所有殘留（page-scroll observer
   * 負責、非 defer）一次 flush 成 revealed。正常頁面元素早被 IO / 頁底保險網接管，計時到
   * 期前殘留集合已空 → 不影響正常進場動畫。defer 元素仍由自管模組處理，不在此集合。 */
  const SAFETY_FLUSH_MS = 3000;
  window.setTimeout(() => {
    document
      .querySelectorAll(`${PAGE_REVEAL_SELECTOR}:not(.is-revealed)`)
      .forEach((el) => {
        applyStaggerVar(el);
        reveal(el);
      });
  }, SAFETY_FLUSH_MS);
}

/* ---------- 動態內容 reobserve hook ---------- */

window.__reobserveReveal = function reobserveReveal() {
  if (PRM || !observer) {
    // 直通分支含 defer 元素（冪等直接顯示）
    document.querySelectorAll('[data-reveal]:not(.is-revealed)').forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  // observer 分支跳過 defer 元素，交由其自管模組 reveal
  const dynamicEls = document.querySelectorAll(`${PAGE_REVEAL_SELECTOR}:not(.is-revealed)`);
  dynamicEls.forEach((el) => {
    applyStaggerVar(el);
    observer.observe(el);
  });
};

/* ---------- lazy image fade-in（沿用 v1，抽成可重複呼叫單元）----------
 * main.css 對 img[loading="lazy"] 設 opacity:0，靠 .is-loaded 才淡入。原本只在 init
 * 掃一次當下存在的圖；動態 render / cloneNode 產生的新 lazy 圖沒被接手 → 永久 opacity:0
 * （圖其實已載入 naturalWidth>0，純 CSS 隱形）。把單張圖的顯示邏輯抽成 bindLazyImages(root)，
 * 讓動態模組（product-compare.js innerHTML 重建後、products.js clone 後）能對新節點再呼叫。
 *
 * 純加法：init 既有「對整份 document 掃一次」的行為不變（下方 bindLazyImages(document) 等價於
 * 原 querySelectorAll 迴圈）；只是同一段邏輯現在也可被外部對局部 root 再呼叫。
 *
 * dedup 用 WeakSet（非 data 屬性）：避免對「已綁但尚未 load」的同一張圖重複掛 load/error
 * listener（重複呼叫 / root 重疊時冪等）。刻意不用 data-* 旗標——cloneNode 會連旗標一起複製
 * 但 listener 不複製，clone 出的圖會帶旗標卻無 listener、被誤判已綁而永停 opacity:0。WeakSet
 * 以「元素參照」為鍵：clone 是全新元素、不在集合內 → 必重新掛 listener，正是 products.js
 * clone 場景所需。已 complete 的圖每次都直接補 .is-loaded（class 冪等）—— 對 cache 命中尤其
 * 關鍵：cached 圖 load 事件不會再 fire，只能靠 complete 檢查立即標記。 */

const lazyBound = new WeakSet();

/**
 * 對 root 內所有 lazy 圖套用 .is-loaded 顯示邏輯（可重複呼叫，冪等）。
 * @param {ParentNode} [root=document] 掃描範圍；動態 render 後傳重建/clone 出的容器。
 */
export function bindLazyImages(root = document) {
  const scope = root || document;
  scope.querySelectorAll('img[loading="lazy"]').forEach((img) => {
    // 已載入（含 cache 命中）：立即顯示，不等 load 事件（cached 圖不會再 fire load）
    if (img.complete && img.naturalWidth > 0) {
      img.classList.add('is-loaded');
      return;
    }
    if (lazyBound.has(img)) return; // 同一元素已掛過 listener，不重複綁（clone 為新元素不受影響）
    lazyBound.add(img);
    img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
    // 載入失敗也 fade-in，避免 alt text 永遠 opacity 0
    img.addEventListener('error', () => img.classList.add('is-loaded'), { once: true });
  });
}

// init：對整份 document 掃一次（等價於原 v1 行為）
bindLazyImages(document);

// 動態模組重綁入口（與 __reobserveReveal 對齊的全域 hook 命名）
window.__reapplyLazyReveal = bindLazyImages;

/* ---------- Hero content 進場（page load，沿用 v1）---------- */

if (!PRM) {
  // 只 stamp 載入時可見的 slide（過濾 [hidden]）；隱藏的 slide 交還 hero-slider.js
  // 在 goTo 時管理，避免雙寫者搶寫導致首訪過場淡入被跳過（race condition）
  const heroContents = document.querySelectorAll('.hero__content:not([hidden])');
  if (heroContents.length) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        heroContents.forEach((el) => el.classList.add('is-hero-revealed'));
      });
    });
  }
}
